import { isIgnoredKey, type ArrayDiffRule, type ComparisonConfig } from './campaign-diff.config';

const isPlainObject = (val: unknown): val is Record<string, unknown> => val != null && typeof val === 'object' && !Array.isArray(val);

const isScalar = (val: unknown): boolean => val === null || typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean';

export const isPlainObjectValue = isPlainObject;

export type ArrayItemContext = {
	itemPath: string;
	rule: ArrayDiffRule<any>;
};

// Resolves `root.<index>[.nested...]` paths of arrays that have a rule in the config.
export const getArrayItemContext = (path: string, config: ComparisonConfig): ArrayItemContext | undefined => {
	const [root, index] = path.split('.');
	const rule = config.arrays[root];
	if (!rule || index === undefined || !/^\d+$/.test(index)) {
		return undefined;
	}
	return { itemPath: `${root}.${index}`, rule };
};

const humanizePathSegment = (segment: string): string => {
	const spaced = segment.replace(/_/g, ' ').trim();
	if (!spaced) {
		return segment;
	}
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

export const sectionTitle = (path: string, config: ComparisonConfig): string => {
	const root = path.split('.')[0] || path;
	return config.sections[root] ?? humanizePathSegment(root);
};

export type Change = { key: string; old: any; new: any };

export type ChangeGroup = {
	parentPath: string;
	leafKeys: string[];
	changes: Change[];
	kind: 'add' | 'remove' | 'edit';
};

export type SectionBucket = {
	title: string;
	groups: ChangeGroup[];
};

export type MinimalField = {
	key: string;
	oldDisplay: string;
	newDisplay: string;
	changed: boolean;
};

export const toPlainRoot = (root: unknown): Record<string, any> | null => {
	if (root == null) {
		return null;
	}
	try {
		return JSON.parse(JSON.stringify(root));
	} catch {
		return null;
	}
};

export const getByPath = (root: unknown, path: string): unknown => {
	if (root == null) {
		return undefined;
	}
	if (!path) {
		return root;
	}
	const parts = path.split('.');
	let current: any = root;
	for (const part of parts) {
		if (current == null || (typeof current !== 'object' && !Array.isArray(current))) {
			return undefined;
		}
		current = current[part];
	}
	return current;
};

const formatDisplayValue = (val: unknown): string => {
	if (val === undefined || val === '') {
		return 'Not specified';
	}
	if (val === null) {
		return 'null';
	}
	if (Array.isArray(val)) {
		if (val.length === 0) {
			return 'None';
		}
		return val.map(item => (item != null && typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
	}
	if (isPlainObject(val)) {
		return JSON.stringify(val);
	}
	return String(val);
};

const valuesEqual = (a: unknown, b: unknown): boolean => {
	if (a === b) {
		return true;
	}
	if (a == null && b == null) {
		return true;
	}
	return false;
};

const fieldEntry = (key: string, oldVal: unknown, newVal: unknown): MinimalField => {
	const changed = !valuesEqual(oldVal, newVal);
	return {
		key,
		oldDisplay: changed ? formatDisplayValue(oldVal) : '...',
		newDisplay: changed ? formatDisplayValue(newVal) : '...',
		changed,
	};
};

export const getEntityLabel = (root: unknown, group: ChangeGroup, config: ComparisonConfig): string | undefined => {
	const context = getArrayItemContext(group.parentPath, config);
	if (!context) {
		return undefined;
	}
	const item = getByPath(root, context.itemPath);
	return isPlainObject(item) ? context.rule.label(item) : undefined;
};

export const groupChangesByParent = (changes: Change[], config: ComparisonConfig): ChangeGroup[] => {
	const groups: ChangeGroup[] = [];
	const indexByPath = new Map<string, number>();

	const pushOrMerge = (parentPath: string, kind: ChangeGroup['kind'], change: Change, leafKey?: string) => {
		const existing = indexByPath.get(parentPath);
		if (existing != null && groups[existing].kind === kind) {
			groups[existing].changes.push(change);
			if (leafKey && !groups[existing].leafKeys.includes(leafKey)) {
				groups[existing].leafKeys.push(leafKey);
			}
			return;
		}
		indexByPath.set(parentPath, groups.length);
		groups.push({
			parentPath,
			leafKeys: leafKey ? [leafKey] : [],
			changes: [change],
			kind,
		});
	};

	for (const change of changes) {
		const itemContext = getArrayItemContext(change.key, config);
		if (itemContext && change.key !== itemContext.itemPath) {
			pushOrMerge(itemContext.itemPath, 'edit', change, change.key.slice(itemContext.itemPath.length + 1));
			continue;
		}

		const isAddedObject = change.old == null && isPlainObject(change.new);
		const isRemovedObject = change.new == null && isPlainObject(change.old);

		if (isAddedObject) {
			pushOrMerge(change.key, 'add', change);
			continue;
		}
		if (isRemovedObject) {
			pushOrMerge(change.key, 'remove', change);
			continue;
		}

		const lastDot = change.key.lastIndexOf('.');
		if (lastDot === -1) {
			pushOrMerge(change.key, 'edit', change, change.key);
			continue;
		}

		const parentPath = change.key.slice(0, lastDot);
		const leafKey = change.key.slice(lastDot + 1);
		pushOrMerge(parentPath, 'edit', change, leafKey);
	}

	return groups;
};

// Shows the rule's fields; falls back to the raw changes when none of them changed (e.g. a nested phase strategy edit).
export const buildArrayItemFields = (context: ArrayItemContext, oldItem: unknown, newItem: unknown, changes: Change[]): MinimalField[] => {
	const oldPlain = isPlainObject(oldItem) ? oldItem : null;
	const newPlain = isPlainObject(newItem) ? newItem : null;
	const ruleFields = Object.entries(context.rule.fields).map(([key, read]) =>
		fieldEntry(key, oldPlain ? read(oldPlain) : undefined, newPlain ? read(newPlain) : undefined)
	);
	if (ruleFields.some(field => field.changed)) {
		return ruleFields;
	}
	return changes.map(change => fieldEntry(change.key.slice(context.itemPath.length + 1) || change.key, change.old, change.new));
};

export const buildMinimalFields = (
	oldObj: unknown,
	newObj: unknown,
	parentPath: string,
	leafKeys: string[],
	changes: Change[],
	ignoreKeys: string[]
): MinimalField[] => {
	const oldPlain = isPlainObject(oldObj) ? oldObj : null;
	const newPlain = isPlainObject(newObj) ? newObj : null;

	if (oldPlain == null && newPlain == null) {
		return leafKeys.map(leafKey => {
			const change = changes.find(c => c.key === parentPath || c.key.endsWith(`.${leafKey}`) || c.key === leafKey);
			return fieldEntry(leafKey, change?.old, change?.new);
		});
	}

	const keys = new Set<string>();
	for (const leaf of leafKeys) {
		keys.add(leaf);
	}
	for (const obj of [oldPlain, newPlain]) {
		if (!obj) {
			continue;
		}
		for (const [k, v] of Object.entries(obj)) {
			if (isScalar(v)) {
				keys.add(k);
			}
		}
	}

	return Array.from(keys)
		.filter(key => !isIgnoredKey(parentPath ? `${parentPath}.${key}` : key, ignoreKeys))
		.slice(0, 8)
		.map(key => fieldEntry(key, oldPlain?.[key], newPlain?.[key]));
};

const forcedNullEntry = (key: string, value: unknown, kind: 'add' | 'remove'): MinimalField => ({
	key,
	oldDisplay: kind === 'add' ? 'null' : formatDisplayValue(value),
	newDisplay: kind === 'add' ? formatDisplayValue(value) : 'null',
	changed: true,
});

export const buildAddedOrRemovedFields = (
	obj: unknown,
	parentPath: string,
	kind: 'add' | 'remove',
	config: ComparisonConfig
): MinimalField[] => {
	if (!isPlainObject(obj)) {
		return [forcedNullEntry(parentPath.split('.').pop() || parentPath, obj, kind)];
	}

	const itemContext = getArrayItemContext(parentPath, config);
	if (itemContext && itemContext.itemPath === parentPath) {
		return Object.entries(itemContext.rule.fields).map(([key, read]) => forcedNullEntry(key, read(obj), kind));
	}

	const scalarKeys = Object.entries(obj)
		.filter(([k, v]) => isScalar(v) && !isIgnoredKey(parentPath ? `${parentPath}.${k}` : k, config.ignoreKeys))
		.slice(0, 8)
		.map(([k]) => k);

	if (scalarKeys.length === 0) {
		return [forcedNullEntry('value', parentPath, kind)];
	}

	return scalarKeys.map(key => forcedNullEntry(key, obj[key], kind));
};

export const groupChangesBySection = (groups: ChangeGroup[], config: ComparisonConfig): SectionBucket[] => {
	const buckets: SectionBucket[] = [];
	const indexByTitle = new Map<string, number>();

	for (const group of groups) {
		const title = sectionTitle(group.parentPath, config);
		const existing = indexByTitle.get(title);
		if (existing != null) {
			buckets[existing].groups.push(group);
			continue;
		}
		indexByTitle.set(title, buckets.length);
		buckets.push({ title, groups: [group] });
	}

	return buckets;
};
